/**
 * ContractsHub — Page Contrats unifiée avec 4 onglets internes.
 *
 * 4 tabs :
 *   ┌─ Annuaire       (liste catalogue complète, filtres, actions)
 *   ├─ Échéances      (fin de contrat + fin d'essai + reconductions imminentes)
 *   ├─ Reconductions  (workflow renewals ClientRenewals)
 *   └─ Import & OCR   (upload PDF simple + batch massif)
 *
 * Bouton killer en header : "🪄 Analyser un PDF" → /app/contracts/intake
 * Onglet actif via ?tab= pour deep-links.
 */
import { useSearchParams, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, Calendar, RefreshCw, Upload } from "lucide-react";
import ContratsAnnuaire from "@/pages/ContratsAnnuaire";
import ContratsEcheances from "@/pages/ContratsEcheances";
import ClientRenewals from "@/pages/ClientRenewals";
import ContratsBulkImport from "@/pages/ContratsBulkImport";

const TAB_ALIASES = {
  annuaire: "annuaire", catalog: "annuaire",
  echeances: "echeances", deadlines: "echeances",
  renewals: "renewals", reconductions: "renewals",
  import: "import", ocr: "import", batch: "import",
};

export default function ContractsHub() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const active = TAB_ALIASES[params.get("tab")] || "annuaire";

  const setTab = (t) => {
    const p = new URLSearchParams(params);
    if (t === "annuaire") p.delete("tab");
    else p.set("tab", t);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="contracts-hub">
      {/* Header avec CTA killer */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Contrats Clients
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Annuaire complet, échéances et imports IA — en un seul endroit.
          </p>
        </div>
        <Button
          onClick={() => nav("/app/contracts/intake")}
          data-testid="contracts-hub-intake-btn"
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-md">
          <Sparkles className="w-4 h-4 mr-1.5" />
          Importer un contrat (PDF)
        </Button>
      </div>

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 h-auto">
          <TabsTrigger value="annuaire" data-testid="contracts-tab-annuaire"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <BookOpen className="h-4 w-4" />
            Annuaire
          </TabsTrigger>
          <TabsTrigger value="echeances" data-testid="contracts-tab-echeances"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Calendar className="h-4 w-4" />
            Échéances
          </TabsTrigger>
          <TabsTrigger value="renewals" data-testid="contracts-tab-renewals"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <RefreshCw className="h-4 w-4" />
            Reconductions
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="contracts-tab-import"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Upload className="h-4 w-4" />
            Import & OCR
          </TabsTrigger>
        </TabsList>

        <TabsContent value="annuaire" className="mt-6"><ContratsAnnuaire embedded /></TabsContent>
        <TabsContent value="echeances" className="mt-6"><ContratsEcheances embedded /></TabsContent>
        <TabsContent value="renewals" className="mt-6"><ClientRenewals embedded /></TabsContent>
        <TabsContent value="import" className="mt-6"><ContratsBulkImport embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
